import type { ChatMessageRow, ChatRunState, ChatSessionRow, ExecutionResource } from '@/api'
import type { UiMode } from '@/composables/useUiMode'

/**
 * The thread as it reads. A tool row is neither what the CEO wrote nor what the member said back: it is
 * what the member did in between, so each one hangs off the reply it produced — or, when no reply followed
 * (a turn that handed work off and stopped), off a log of its own — rather than taking a bubble of its own
 * in the conversation.
 */
export type ThreadEntry =
    | { kind: 'message'; key: string; row: ChatMessageRow; tools: ChatMessageRow[] }
    | { kind: 'tools'; key: string; tools: ChatMessageRow[] }

export function threadEntries(rows: ChatMessageRow[]): ThreadEntry[] {
    const entries: ThreadEntry[] = []
    let tools: ChatMessageRow[] = []
    const flush = () => {
        const first = tools[0]
        if (first) {
            entries.push({ kind: 'tools', key: `tools-${first.id}`, tools })
            tools = []
        }
    }
    // A call still waiting on you is drawn as its own card at the end of the thread, not in a tool log.
    for (const row of rows.filter(candidate => !awaitingConfirmation(candidate))) {
        if (row.role === 'tool') {
            tools.push(row)
        } else if (row.role === 'assistant') {
            entries.push({ kind: 'message', key: row.id, row, tools })
            tools = []
        } else {
            flush()
            entries.push({ kind: 'message', key: row.id, row, tools: [] })
        }
    }
    flush()
    return entries
}

/** One tool call as the log shows it: what it did, the run it started if any, and what came back. */
export type ChatToolItem = {
    id: string
    /** The tool's name, or a neutral placeholder: a call with nothing else to show still names itself (#225). */
    tool: string
    runId: string | null
    slug: string | null
    /** The linked run's status line, live where the thread is tracking that run. */
    status: string | null
    /**
     * The linked run's status word, which a group of runs counts by (#1761). `status` may instead spell out why the
     * tracked run failed or is blocked; left out, the group counts `status`.
     */
    result?: string | null
    /** The run as a card names it, `<task name> · <result>` (#1761): the task name, else the slug in Advanced, else "Task unknown". */
    taskLabel?: string | null
    resources: ExecutionResource[]
    /** What the log opens to: the row's payload when it has fields, else its content, else nothing. */
    payload: unknown
}

export function toolRunId(row: ChatMessageRow): string | null {
    const id = row.payload?.runId
    return typeof id === 'string' ? id : null
}

/** What a run card is called when its task's name is unknown, in either mode. */
export const TASK_UNKNOWN = 'Task unknown'

/**
 * What a run card calls the run a tool row started (#1761): its task's name, as the chat read serves it on the row.
 * With no name, Advanced falls back to the task's slug (served, else the one the row recorded) and Standard, which
 * never shows a slug (#1749), says "Task unknown", as Advanced does with no slug either.
 */
export function runTaskLabel(run: ChatRunState | null | undefined, recordedSlug: string | null, mode: UiMode): string {
    if (run?.taskName) {
        return run.taskName
    }
    const slug = run?.taskSlug || recordedSlug
    return mode === 'advanced' && slug ? slug : TASK_UNKNOWN
}

/**
 * The gate a message was posted for (#229). A CEO question raised by a run lands in the assigned member's
 * thread carrying `payload.gateId`, and nothing else links it to this conversation — the run behind it was
 * never started from here, so `toolRunId` finds nothing. This is what lets the thread render the decision
 * for it, instead of showing the question as text the CEO cannot answer.
 */
export function messageGateId(row: ChatMessageRow): string | null {
    const id = row.payload?.gateId
    return typeof id === 'string' ? id : null
}

/**
 * #849: the run whose proposed work items a lead's message put to the person (`payload.proposedWork.runId`), or null
 * for any other message. The card under it reads that run's batch live.
 */
export function messageProposedRunId(row: ChatMessageRow): string | null {
    const proposed = row.payload?.['proposedWork']
    const runId = proposed && typeof proposed === 'object' ? (proposed as { runId?: unknown }).runId : undefined
    return row.role === 'assistant' && typeof runId === 'string' && runId !== '' ? runId : null
}

/**
 * What the thread says under a reply that did not simply finish (`payload.ended`): the member used every step one
 * reply allows, or the model's length limit cut it off. Null for a reply that finished. A reply that used every step
 * with work left on the member's step list carries on by itself in a follow-up turn (`payload.carryOn`, #285), so
 * nobody is asked to write. The note is fixed when the reply ends, and the follow-up can still be dropped after it
 * (Stop, a spend pause, a failed turn), so it says what will happen unless the person stops it, never that it is
 * happening: a stop then shows its own line below it.
 */
export function replyEndedNote(row: ChatMessageRow): string | null {
    switch (row.payload?.ended) {
        case 'step_limit':
            return row.payload?.carryOn === true
                ? 'Stopped at the step limit. Will carry on by itself unless you press Stop.'
                : 'Stopped at the step limit. Write to carry on.'
        case 'length':
            return 'Cut off by the model’s length limit.'
        default:
            return null
    }
}

/** What a person's message row records about the enterprise's memory (`payload.memory`), read defensively. */
function memoryRecord(row: ChatMessageRow): Record<string, unknown> | null {
    const memory = row.payload?.memory
    return memory && typeof memory === 'object' ? (memory as Record<string, unknown>) : null
}

/**
 * What the thread says under a person's message about the enterprise's memory: how many notes were recalled for
 * it, or that memory could not be read. Null when nothing was recalled, and for a message sent where no memory
 * is connected or the sender may not read it.
 */
export function memoryNote(row: ChatMessageRow): string | null {
    const memory = memoryRecord(row)
    if (memory?.outcome === 'unavailable') {
        return 'Memory was unavailable for this message.'
    }
    const recalled = memory?.evidenceCount
    if (memory?.outcome === 'consulted' && typeof recalled === 'number' && recalled > 0) {
        return `Memory: ${recalled} ${recalled === 1 ? 'note' : 'notes'} recalled`
    }
    return null
}

/** One note memory recalled for a message, as the thread lists it. */
export type RecalledNote = {
    key: string
    /** The note's first line. */
    title: string
    /** What became of it this turn, in words. */
    statusLabel: string
    /** Where the note lives: its entry on the Memory page, or the run whose lesson it is. Null when unknown. */
    to: string | null
    openLabel: string
}

const RECALLED_STATUS: Record<string, string> = {
    shown: 'Shown to the member',
    already_shown: 'Already shown earlier in this chat',
    left_out: 'Left out: no room',
}

/** A run's own lesson is retained under its after-work key, `run:<run id>:after`. */
const RUN_LESSON = /^run:([^:]+):after$/

/**
 * The notes memory recalled for a person's message, best match first, each with where to open it. Empty for a
 * message recorded before the thread kept the list, and for one memory recalled nothing for.
 */
export function recalledNotes(row: ChatMessageRow): RecalledNote[] {
    const notes = memoryRecord(row)?.notes
    if (!Array.isArray(notes)) {
        return []
    }
    return notes.flatMap((note: unknown, index): RecalledNote[] => {
        if (!note || typeof note !== 'object') {
            return []
        }
        const { id, title, status } = note as { id?: unknown; title?: unknown; status?: unknown }
        const place = noteLink(id)
        return [
            {
                key: `${index}:${typeof id === 'string' ? id : ''}`,
                title: typeof title === 'string' && title !== '' ? title : 'A note with no first line',
                statusLabel: RECALLED_STATUS[String(status)] ?? 'Recalled',
                to: place.to,
                openLabel: place.openLabel,
            },
        ]
    })
}

/** Where a recalled note lives, from the document it was retained under: the run it is a lesson of, or its entry. */
function noteLink(id: unknown): { to: string | null; openLabel: string } {
    if (typeof id !== 'string' || id === '') {
        return { to: null, openLabel: 'Open note' }
    }
    const run = RUN_LESSON.exec(id)?.[1]
    return run
        ? { to: `/runs/${run}`, openLabel: 'Open run' }
        : { to: `/memory?note=${encodeURIComponent(id)}`, openLabel: 'Open note' }
}

/**
 * A connected tool call the member is waiting on you for: its server does not say it only reads, so it asks
 * before it runs. The row's payload becomes the call's result once you decide.
 */
export function awaitingConfirmation(row: ChatMessageRow): boolean {
    return row.role === 'tool' && row.payload?.awaiting === 'confirmation'
}

/**
 * Whether a waiting call's card offers "Always allow". A write to the organisation (`os.create_role` and the rest)
 * asks every time, and its row says so with `alwaysAllow: false`.
 */
export function offersAlwaysAllow(row: ChatMessageRow): boolean {
    return row.payload?.alwaysAllow !== false
}

/** The arguments a waiting call will run with, one `key: value` line each, as its card lists them. */
export function confirmationLines(row: ChatMessageRow): string[] {
    const args = row.payload?.arguments
    if (!args || typeof args !== 'object') {
        return []
    }
    return Object.entries(args as Record<string, unknown>).map(
        ([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`,
    )
}

export function toolPayload(row: ChatMessageRow): unknown {
    if (row.payload && Object.keys(row.payload).length > 0) {
        return row.payload
    }
    const content = row.content?.trim()
    return content ? content : null
}

/**
 * When each member was last said something to or by, in epoch ms: the newest `lastMessage` across every listed
 * session with them, open or closed. That value already leaves tool traffic out. A session's `updatedAt` is not
 * used: a read, a close or a status change moves it with nothing said. A member with no messages has no entry.
 */
export function lastMessageAtByMember(sessions: readonly ChatSessionRow[]): Map<string, number> {
    const latest = new Map<string, number>()
    for (const session of sessions) {
        const said = session.lastMessage ? Date.parse(session.lastMessage.createdAt) : Number.NaN
        if (Number.isNaN(said)) {
            continue
        }
        const known = latest.get(session.memberId)
        if (known === undefined || said > known) {
            latest.set(session.memberId, said)
        }
    }
    return latest
}

/**
 * The member list in the order Chat draws it, on desktop and off-canvas alike: newest message first, then every
 * member with no messages. Ties, equal times or no messages at all, keep the directory's order, so the same data
 * always draws the same list.
 */
export function byLatestMessage<T extends { id: string }>(
    directory: readonly T[],
    sessions: readonly ChatSessionRow[],
): T[] {
    const latest = lastMessageAtByMember(sessions)
    return directory
        .map((member, index) => ({ member, index, said: latest.get(member.id) ?? Number.NEGATIVE_INFINITY }))
        .sort((a, b) => (a.said === b.said ? a.index - b.index : b.said - a.said))
        .map(({ member }) => member)
}
